import { toCalendarDateTime } from "@internationalized/date";
import { useMutation } from "@tanstack/react-query";
import {
  CheckmarkFill18Icon,
  CloseFill24Icon,
} from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { format } from "date-fns";
import { EditProductionDeviationButton } from "features/CenterContent/RoleContent/ProductionDeviation/components/EditProductionDeviationButton";
import { ProductionDeviationResponse } from "features/CenterContent/RoleContent/ProductionDeviation/types/responseSchema";
import { Badges } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/Badges";
import { TrainSerieDisplay } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/TrainSerieDisplay";
import { TrainTypeDisplay } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/components/TrainTypeDisplay";
import { productionDeviationQueryKey } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useProductionDeviation";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessageWithRetryButton } from "shared/components/feedback/FailureMessageWithRetryButton";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import { Box, DropsButton, HStack, Stack } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { css, useTheme } from "styled-components";

type ConfirmAction = "close" | "depublish";

type ProductionDeviationElementProps = {
  productionDeviation: ProductionDeviationResponse;
};

export const ProductionDeviationElement: FC<
  ProductionDeviationElementProps
> = ({ productionDeviation }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );
  const {
    reference,
    trainIds,
    eventType,
    updatedAt,
    fromDate,
    toDate,
    countryCode,
  } = productionDeviation;
  const durations = {
    fromTime: toCalendarDateTime(fromDate),
    toTime: toCalendarDateTime(toDate),
  };
  const closeMutation = useMutation({
    mutationFn: () =>
      mutationFnPOST(
        `${getBackendUrl()}/production-deviation/${reference}/close`,
      ),
    onSuccess: () => {
      setConfirmAction(null);
      void queryClient.invalidateQueries({
        queryKey: productionDeviationQueryKey(countryCode),
      });
    },
  });
  const depublishMutation = useMutation({
    mutationFn: () =>
      mutationFnPOST(
        `${getBackendUrl()}/production-deviation/${reference}/depublish`,
      ),
    onSuccess: () => {
      setConfirmAction(null);
      void queryClient.invalidateQueries({
        queryKey: productionDeviationQueryKey(countryCode),
      });
    },
  });

  const confirmButtons = (onConfirm: () => void, isPending: boolean) => (
    <HStack w="124px" h="30px" gap="4px">
      <DropsButton
        variant="primary"
        semantics="alarm"
        size="xs"
        w="30px"
        h="30px"
        minW="auto"
        p="0"
        onClick={() => setConfirmAction(null)}
        aria-label={t("Avbryt")}
      >
        <Box color="white">
          <CloseFill24Icon />
        </Box>
      </DropsButton>
      <DropsButton
        variant="primary"
        semantics="success"
        size="xs"
        onClick={onConfirm}
        loading={isPending}
        leftIcon={<CheckmarkFill18Icon />}
      >
        {t("Bekreft")}
      </DropsButton>
    </HStack>
  );

  const renderDepublish = () => {
    if (depublishMutation.isError)
      return (
        <FailureMessageWithRetryButton
          customMessage={t("Kunne ikke depublisere hendelsen")}
          retryFunction={() => depublishMutation.mutate()}
        />
      );
    if (confirmAction === "depublish")
      return confirmButtons(
        () => depublishMutation.mutate(),
        depublishMutation.isPending,
      );
    return (
      <DropsButton
        size="xs"
        variant="tertiary"
        onClick={() => setConfirmAction("depublish")}
      >
        {t("Depubliser")}
      </DropsButton>
    );
  };

  const renderClose = () => {
    if (closeMutation.isError)
      return (
        <FailureMessageWithRetryButton
          customMessage={t("Kunne ikke friskmelde hendelsen")}
          retryFunction={() => closeMutation.mutate()}
        />
      );
    if (confirmAction === "close")
      return confirmButtons(
        () => closeMutation.mutate(),
        closeMutation.isPending,
      );
    return (
      <DropsButton
        size="xs"
        variant="tertiary"
        onClick={() => setConfirmAction("close")}
      >
        {t("Friskmeld")}
      </DropsButton>
    );
  };

  return (
    <Stack key={reference}>
      <SegmentedPanel
        panelStyle={css`
          background: ${theme.colorBackgroundTertiary};
          border-radius: 12px;
          padding: 18px;
          gap: 18px;
        `}
        dashedLineVariant="tertiary"
      >
        <Stack>
          <HStack w="100%" justifyContent="space-between">
            <HStack>
              <TrainSerieDisplay trainIds={trainIds} />
              <TrainTypeDisplay eventType={eventType} />
            </HStack>
            <HStack>
              <EditProductionDeviationButton uuid={reference} />
              {renderDepublish()}
              {renderClose()}
            </HStack>
          </HStack>

          <HStack>
            <Badges eventCategory="productionDeviation" />
            <Badges eventCategory="train" />
          </HStack>
          <Badges displayDuration={durations} />
          <Text secondary>
            {format(
              updatedAt.toDate(),
              "'Sist oppdatert' dd.MM.yyyy 'kl.' HH:mm",
            )}
          </Text>
        </Stack>
      </SegmentedPanel>
    </Stack>
  );
};
